import { Icon } from "@iconify/react";
import { useTheme } from "../../../hooks/useTheme";
import { Colors } from "../../../utils/colors";
import SwitchToggleItem from "../../SwitchToggleItem";
import {
  AccountData,
  MembershipPromoCode,
  MembershipPromoCodeSource,
  NotificationType,
} from "@markit/common.types";
import { makeMembershipPromoCode } from "@markit/common.utils";
import NumberInput from "../../NumberInput";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { API } from "../../../API";
import { MixpanelContext } from "../../../context/AnalyticsService";
import PopupModalContainer, {
  PopupModalContainerWidth,
} from "../../Containers/PopupModalContainer";
import CustomTextField from "../../CustomTextField";
import RectangleButton from "../../Buttons/RectangleButton";
import AlertContainer from "../../Containers/AlertContainer";
import { showNotificationBanner } from "../../../utils/notificationUtils";
import { useDispatch } from "react-redux";
import { HorizontalDivider } from "../../Dividers/HorizontalDivider";
import { checkActiveMembershipPromoCodeAliasExists } from "../../../utils/membershipUtils";
import DropdownArrowIcon from "../../DropdownsAndTabs/DropdownArrowIcon";

type MembershipPromoCodeModalProps = {
  accountData: AccountData;
  setIsPromoModalVisible: (promoModalVisible: boolean) => void;
  existingPromo?: MembershipPromoCode;
};

export const MembershipPromoCodeModal = (
  props: MembershipPromoCodeModalProps
) => {
  const { accountData, setIsPromoModalVisible, existingPromo } = props;
  const { theme } = useTheme();
  const mixpanel = useContext(MixpanelContext);
  const dispatch = useDispatch();
  const [alias, setAlias] = useState(existingPromo ? existingPromo.alias : "");
  const [discount, setDiscount] = useState<number | undefined>(
    existingPromo ? existingPromo.value : 20
  );
  const [maxUses, setMaxUses] = useState<number | undefined>(
    existingPromo ? existingPromo.maxRedemptions : 25
  );
  const [durationMinimized, setDurationMinimized] = useState(!existingPromo);
  const [discountDuration, setDiscountDuration] = useState<number | undefined>(
    existingPromo ? existingPromo.durationInBillingCycles : 0
  );
  const [savingChanges, setSavingChanges] = useState(false);
  const [alertText, setAlertText] = useState({ heading: "", subHeading: "" });

  const styles = {
    secondaryButton: {
      whiteSpace: "nowrap",
      padding: 14,
      backgroundColor: Colors.WHITE1,
      border: `0.5px solid ${Colors.GRAY2}`,
    },
    inputContainer: { padding: "10px 14px", borderRadius: 12, width: 120 },
  };

  const aliasUppercase = useMemo(() => alias.toUpperCase(), [alias]);

  const resetFields = useCallback(() => {
    setIsPromoModalVisible(false);
    setAlias("");
    setDiscount(20);
    setMaxUses(25);
  }, [setIsPromoModalVisible]);

  const savePromoCodePress = useCallback(async () => {
    const membershipPromoCode: MembershipPromoCode = makeMembershipPromoCode(
      accountData.uid,
      aliasUppercase,
      discount ?? 20,
      discountDuration ?? 0,
      maxUses ?? 25,
      MembershipPromoCodeSource.CREATOR,
      ""
    );

    await API.user
      .createMembershipPromoCode({
        uid: accountData.uid,
        membershipPromoCode: membershipPromoCode,
      })
      .then((response) => {
        if (!response.membershipPromoCode) {
          setAlertText({
            heading: "Promo Code Created Incorrectly.",
            subHeading: "Please try again.",
          });
        } else {
          showNotificationBanner(
            dispatch,
            "Promo Code Created!",
            NotificationType.AFFIRMATIVE
          );
          resetFields();
        }
      })
      .catch(() => {
        setAlertText({
          heading: "Something went wrong.",
          subHeading: "Please try again.",
        });
      });
  }, [
    accountData.uid,
    aliasUppercase,
    discount,
    discountDuration,
    dispatch,
    maxUses,
    resetFields,
  ]);

  const saveEditsPromoCodePress = useCallback(async () => {
    if (!existingPromo) {
      return;
    }

    const partialMembershipPromoCode: Partial<MembershipPromoCode> = {
      alias: aliasUppercase,
    };

    await API.user
      .editMembershipPromoCode({
        uid: accountData.uid,
        membershipPromoCodeId: existingPromo.id,
        updates: partialMembershipPromoCode,
      })
      .then((response) => {
        if (!response.membershipPromoCode) {
          setAlertText({
            heading: "Promo Code Updated Incorrectly.",
            subHeading: "Please try again.",
          });
        } else {
          showNotificationBanner(
            dispatch,
            "Promo Code Updated!",
            NotificationType.AFFIRMATIVE
          );
          resetFields();
        }
      })
      .catch(() => {
        setAlertText({
          heading: "Something went wrong.",
          subHeading: "Please try again.",
        });
      });
  }, [accountData.uid, aliasUppercase, dispatch, existingPromo, resetFields]);

  const onSavePress = useCallback(async () => {
    if (/\s/.test(alias)) {
      setAlertText({
        heading: "Promo codes can't include spaces",
        subHeading: "",
      });
      return;
    } else if (
      discount &&
      (!Number.isInteger(discount) || discount < 1 || discount > 100)
    ) {
      setAlertText({
        heading: "Invalid discount",
        subHeading: "Discount must be a full integer between 1% and 100%",
      });
      return;
    } else if (
      maxUses &&
      (!Number.isInteger(maxUses) || maxUses < 1 || maxUses > 10000)
    ) {
      setAlertText({
        heading: "Invalid max uses",
        subHeading: "Max uses must be a full integer between 1 and 10,000",
      });
      return;
    } else if (
      discountDuration &&
      (!Number.isInteger(discountDuration) ||
        discountDuration < 1 ||
        discountDuration > 12)
    ) {
      setAlertText({
        heading: "Invalid discount duration",
        subHeading:
          "Discount duration must be a full integer between 1 and 12 months",
      });
      return;
    }

    // Check if the alias already exists among active promo codes
    const aliasExists = await checkActiveMembershipPromoCodeAliasExists(
      accountData.uid,
      aliasUppercase
    );
    if (aliasExists) {
      setAlertText({
        heading: "Promo code with name already exists.",
        subHeading: "Please choose a new name.",
      });
      return;
    }

    setSavingChanges(true);
    if (existingPromo) {
      await saveEditsPromoCodePress();
    } else {
      await savePromoCodePress();
    }

    setSavingChanges(false);
  }, [
    accountData.uid,
    alias,
    aliasUppercase,
    discount,
    discountDuration,
    existingPromo,
    maxUses,
    saveEditsPromoCodePress,
    savePromoCodePress,
  ]);

  return (
    <>
      <PopupModalContainer
        headerComp={existingPromo ? "Edit Promo Code" : "New Promo Code"}
        headerLeftIcon={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              paddingInline: "14px",
              marginBottom: "10px",
            }}
          >
            <Icon
              icon={"ion:pricetags"}
              height={35}
              style={{
                color: Colors.BLACK,
              }}
            />
          </div>
        }
        subHeaderComp={
          <div
            style={{
              fontWeight: "400",
              fontSize: 14,
              color: Colors.GRAY1,
            }}
          >
            {existingPromo
              ? "You can only edit the name of your promo code. To edit other fields, we recommend making a new promo code."
              : "Create a new promo code/discount for your paid membership plans"}
          </div>
        }
        noExit={true}
        valueComp={
          <div className="ColumnNormal" style={{ gap: 20 }}>
            <div className="ColumnNormal" style={{ gap: 7 }}>
              <div className="AlignedRowSpaced">
                <span className="bodyMedium">Name</span>
                <span className="smallBodySubtext">{alias.length}/30</span>
              </div>
              <CustomTextField
                value={alias}
                placeholder="Promo Code Name..."
                inputMode="text"
                borderRadius={12}
                backgroundColor={Colors.WHITE}
                onChange={(change: any) => {
                  setAlias(change.target.value);
                }}
                altPadding={14}
                altMarginBottom={0}
                maxLength={30}
              />
              <span
                className="smallBodySubtext"
                style={{ color: Colors.GRAY2 }}
              >
                No spaces, lower case letters, or special characters
              </span>
            </div>
            <div className="AlignedRowSpaced">
              <div>
                <div
                  className="bodyMedium"
                  style={{
                    ...theme.PrimaryText,
                    opacity: existingPromo ? 0.3 : 1,
                  }}
                >
                  Percent Off
                </div>
                <div
                  className="smallBodySubtext"
                  style={{
                    ...theme.SecondaryText,
                    marginTop: 4,
                    opacity: existingPromo ? 0.3 : 1,
                  }}
                >
                  Set to 100% for free subscription
                </div>
              </div>
              <NumberInput
                min={1}
                max={100}
                value={discount}
                onChange={(value) => setDiscount(value)}
                disabled={!!existingPromo}
                prefix={"%"}
                containerStyles={styles.inputContainer}
              />
            </div>
            <div className="AlignedRowSpaced">
              <div
                className="bodyMedium"
                style={{
                  ...theme.PrimaryText,
                  opacity: existingPromo ? 0.3 : 1,
                }}
              >
                Set Max Uses
              </div>
              <NumberInput
                min={1}
                max={10000}
                value={maxUses}
                onChange={(value) => setMaxUses(value)}
                disabled={!!existingPromo}
                containerStyles={styles.inputContainer}
              />
            </div>
            <div
              className={
                existingPromo ? "AlignedRowSpaced" : "AlignedRowSpacedSelect"
              }
              onClick={() => {
                if (!existingPromo) {
                  setDurationMinimized(!durationMinimized);
                }
              }}
            >
              <div>
                <div
                  className="bodyMedium"
                  style={{
                    ...theme.PrimaryText,
                    opacity: existingPromo ? 0.3 : 1,
                  }}
                >
                  Discount Duration
                </div>
                <div
                  className="smallBodySubtext"
                  style={{
                    ...theme.SecondaryText,
                    marginTop: 4,
                    opacity: existingPromo ? 0.3 : 1,
                  }}
                >
                  Set how many months subscription discount lasts
                </div>
              </div>
              <DropdownArrowIcon
                isOpen={!durationMinimized}
                height={20}
                color={Colors.BLACK}
                style={{ opacity: existingPromo ? 0.3 : 1 }}
              />
            </div>
            {!durationMinimized ? (
              <div className="ColumnNormal" style={{ gap: 20 }}>
                <SwitchToggleItem
                  title="Lifetime Discount"
                  description="This discount never expires, and will apply to every month of the subscription."
                  toggleValue={discountDuration === 0}
                  onChange={() => {
                    setDiscountDuration(discountDuration === 0 ? 1 : 0);
                  }}
                  theme={theme}
                  disabled={!!existingPromo}
                />
                <div className="AlignedRowSpaced" style={{ gap: 40 }}>
                  <div>
                    <div
                      className="bodyMedium"
                      style={{
                        ...theme.PrimaryText,
                        opacity:
                          existingPromo || discountDuration === 0 ? 0.3 : 1,
                      }}
                    >
                      Set Duration
                    </div>
                    <div
                      className="smallBodySubtext"
                      style={{
                        ...theme.SecondaryText,
                        marginTop: 4,
                        opacity:
                          existingPromo || discountDuration === 0 ? 0.3 : 1,
                      }}
                    >
                      Subscribers will be charged full price after the set
                      discount duration ends
                    </div>
                  </div>
                  <NumberInput
                    min={1}
                    max={12}
                    value={discountDuration}
                    onChange={(value) => setDiscountDuration(value)}
                    disabled={!!existingPromo || discountDuration === 0}
                    prefix={<div className="smallBodySubtext">months</div>}
                    containerStyles={styles.inputContainer}
                  />
                </div>
              </div>
            ) : null}
          </div>
        }
        fixedBottomContent={
          <div className="ColumnNormal">
            <HorizontalDivider altMargin={14} />
            <div className="AlignedRow" style={{ gap: 10, paddingInline: 14 }}>
              <RectangleButton
                buttonLabel={"Cancel"}
                onPress={resetFields}
                altTextColor={Colors.BLACK}
                altColor={Colors.GRAY11}
                containerStyles={styles.secondaryButton}
              />
              <RectangleButton
                buttonLabel={existingPromo ? "Save" : "Create Code"}
                onPress={onSavePress}
                altTextColor={Colors.WHITE}
                altColor={Colors.BLACK}
                disabled={
                  alias.trim().length === 0 ||
                  (existingPromo && existingPromo.alias === alias.toUpperCase())
                }
                containerStyles={{ paddingBlock: 14, paddingInline: 12 }}
                loading={savingChanges}
              />
            </div>
          </div>
        }
        theme={theme}
        closeModal={resetFields}
        modalContentWidth={PopupModalContainerWidth.SMALL}
      />
      <AlertContainer
        headerComp={alertText.heading}
        subHeaderComp={
          alertText.subHeading !== "" ? alertText.subHeading : undefined
        }
        theme={theme}
        closeModal={() => setAlertText({ heading: "", subHeading: "" })}
        hideModal={alertText.heading === "" && alertText.subHeading === ""}
      />
    </>
  );
};
